/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { Product } from '@atlassian/edition-awareness/types';
import {
	fetchEditionAwarenessPillDismissed,
	setEditionAwarenessPillDismissed,
} from '../../utils/index.tsx';

export const useIsEditionAwarenessPillDismissed = (
	product: Product | undefined,
): {
	hasFetched: boolean;
	isDismissed: boolean;
	handleDismiss: () => void;
} => {
	const [hasFetched, setHasFetched] = useState(false);
	const [isDismissed, setIsDismissed] = useState(true);

	const canDismiss =
		product === 'jira-product-discovery'
			? fg('upsell_edition_awareness_jpd_fg')
			: fg('edition_awareness_upsell_jira_ab_fg');

	useEffect(() => {
		if (canDismiss) {
			fetchEditionAwarenessPillDismissed().then((result) => {
				// if result is undefined, it means the call failed and we can't be certain if the user dismissed the pill or not
				if (result !== undefined) {
					setIsDismissed(!!result);
					setHasFetched(true);
				}
			});
		}
	}, [canDismiss]);

	const handleDismiss = useCallback((): void => {
		if (canDismiss) {
			setIsDismissed(true);
			setEditionAwarenessPillDismissed(true);
		}
	}, [canDismiss]);

	return { hasFetched, isDismissed, handleDismiss };
};

export const useEditionAwarenessStandardToPremium = (
	isSiteAdmin: boolean,
): {
	isLoading: boolean;
	isDismissed: boolean;
	handleDismiss: () => void;
} => {
	// The modal is only displayed to siteAdmins, there's no need to fetch this information for all users
	const [isLoading, setIsLoading] = useState(isSiteAdmin);
	// Assume is dismissed, so we don't show the button then hide it
	const [isDismissed, setIsDismissed] = useState(true);

	useEffect(() => {
		if (!isSiteAdmin) {
			return;
		}

		fetchEditionAwarenessPillDismissed().then((result) => {
			// if result is undefined, it means the call failed and we can't be certain if the user dismissed the pill or not
			if (result !== undefined) {
				setIsDismissed(!!result);
			}
			setIsLoading(false);
		});
	}, [isSiteAdmin]);

	const handleDismiss = useCallback((): void => {
		setIsDismissed(true);
		setEditionAwarenessPillDismissed(true);
	}, []);

	return { isLoading, isDismissed, handleDismiss };
};
